import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';

import {
	makeStyles,
	Link,
	Grid,
	Button
} from '@material-ui/core';

import Fido from "../utils/Fido";
import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import FormHelper from "../utils/FormHelper";
import PaddedLayout from "../components/PaddedLayout";
import GeneralSettingsPanel from "../components/GeneralSettingsPanel";
import DeliveryProfileSettings from "../components/DeliveryProfileSettings";
import RealtimeNotificationSettings from "../components/RealtimeNotificationSettings";
import EmailDigestSettings from "../components/EmailDigestSettings";

const styles = makeStyles(theme => ({
	link: {
		cursor: "pointer",
		fontSize: "1rem",
		marginBottom: 3
	},

	buttonGrid: {
		padding: theme.spacing(3),
		border: `1px solid ${theme.palette.secondary.lightGrey}`,
		borderRadius: "10px"
	
	},

	paddedLayout: {
		[theme.breakpoints.down('xs')]: {
			paddingTop: theme.spacing(2)
		}
	}
}));

function GeneralSettings(props) {
	const authenticatedUser = useContext(AuthorizedUserContext);

	const [selectedPanel, setSelectedPanel] = useState();
	const [libraryTopics, setLibraryTopics] = useState();
	const [rtNotifications, setRtNotifications] = useState([]);
	const [settings, setSettings] = useState({
		user: {},
		times: {},
		notification: {
			text_alerts: 0,
			email_alerts: 0,
			text_gmail: 0,
			email_gmail: 0,
			text_exchange: 0,
			email_exchange: 0,
			text_shareit: 0,
			email_shareit: 0,
			monday_summary: 0,
			tuesday_summary: 0,
			wednesday_summary: 0,
			thursday_summary: 0,
			friday_summary: 0,
			saturday_summary: 0,
			sunday_summary: 0,
			daily_summary_time: "00:00:00",
			daily_summary_send_blank: 0,
			weekly_summary: 0,
			monthly_summary: 0,
			activeLibraryTopics: [],
			alert_schedule: 0,
			alert_schedule_type: "same",
			monday_alerts: 1,
			tuesday_alerts: 1,
			wednesday_alerts: 1,
			thursday_alerts: 1,
			friday_alerts: 1,
			saturday_alerts: 0,
			sunday_alerts: 0,
			email_escalation: 0,
			text_escalation: 0
		}
	});

	let classes = styles(),
		{updateHeader, resetHeader, showSnackbar} = props;

	// update header
	useEffect(() => {
		updateHeader({title: "general settings", primary: true});
		return () => resetHeader();
	}, [updateHeader, resetHeader]);

	// load library topics
	useEffect(() => {
		let fido = new Fido();
		(async function loadLibraryTopics() {
			//setLibraryTopics(null);
			let data = await fido.fetch("/activities/library-topics")
				.catch(error => {
					return showSnackbar(error.message);
				});

			if (data) {
				setLibraryTopics(data);
			}
		})();

		return () => fido.dropIt();
	}, [setLibraryTopics, showSnackbar]);

	// load user settings
	useEffect(() => {
		let fido = new Fido();
		(async function loadUserSettings() {
			let data = await fido.fetch(`${authenticatedUser.profileUrlBase}/settings`)
				.catch(error => {
					return showSnackbar(error.message);
				});

			if (data) {
				setSettings(data);
			}
		})();

		return () => fido.dropIt();
	}, [setSettings, authenticatedUser, showSnackbar]);

	useEffect(() => {
		let rtArray = [];

		if (AuthorizedUser.hasSocialMedia(authenticatedUser)) {
			rtArray.push("text_alerts");
			rtArray.push("email_alerts");
		}

		if (AuthorizedUser.hasGSuite(authenticatedUser)) {
			rtArray.push("text_gmail");
			rtArray.push("email_gmail");
		}

		if (AuthorizedUser.hasExchange(authenticatedUser)) {
			rtArray.push("text_exchange");
			rtArray.push("email_exchange");
		}

		if (AuthorizedUser.hasShareit(authenticatedUser)) {
			rtArray.push("text_shareit");
			rtArray.push("email_shareit");
		}

		if (AuthorizedUser.hasWebSearch(authenticatedUser)) {
			rtArray.push("text_web_search");
			rtArray.push("email_web_search");
		}

		setRtNotifications(rtArray);
	}, [setRtNotifications, authenticatedUser]);

	const panelClicked = panel => (event, isExpanded) => {
		setSelectedPanel(isExpanded ? panel : false);
	};

	const hasRealTimeNotification = () => {
		return rtNotifications.some(nId => settings.notification[nId]);
	};

	const alertDays = [
		"monday_alerts",
		"tuesday_alerts",
		"wednesday_alerts",
		"thursday_alerts",
		"friday_alerts",
		"saturday_alerts",
		"sunday_alerts"
	];

	const digestDays = [
		"monday_summary",
		"tuesday_summary",
		"wednesday_summary",
		"thursday_summary",
		"friday_summary",
		"saturday_summary",
		"sunday_summary"
	];

	const alertDaysButtonToggled = (index) => {
		let day = alertDays[index],
			stateSettings = Object.assign({}, settings);
		stateSettings.notification[day] = !stateSettings.notification[day];
		setSettings(stateSettings);
	};

	const digestButtonToggled = (index) => {
		let day = digestDays[index],
			stateSettings = Object.assign({}, settings);
		stateSettings.notification[day] = !stateSettings.notification[day];
		setSettings(stateSettings);
	};

	const gotoProfile = () => {
		props.history.push(`/profile/${authenticatedUser.id}`);
	};

	const handleFormChange = (event, libraryTopicObject) => {
		let {name, value} = event.target,
			stateSettings = Object.assign({}, settings),
			notification = stateSettings.notification,
			activeLibraryTopics = notification.activeLibraryTopics,
			change = FormHelper.getChanged(event);

		// if a Library Topic notification change
		if (libraryTopicObject != null && libraryTopicObject.topic != null) {
			libraryTopicObject = libraryTopicObject.topic;

			let filteredArray,
				changeValue = change[libraryTopicObject.id];

			if (changeValue === false) {
				filteredArray = activeLibraryTopics.filter(topic => topic.id !== libraryTopicObject.id);
				notification.activeLibraryTopics = filteredArray;
			} else {
				libraryTopicObject.active = true;
				notification.activeLibraryTopics.push(libraryTopicObject);
			}

			// if all topic notifications are turned off, turn off all real time notifications
			if (notification.activeLibraryTopics.length === 0) {
				rtNotifications.forEach(nId => notification[nId] = false);
				notification.alert_schedule = false;
			}
		} else {
			notification = Object.assign(notification, change);
			// if all real time notifications off, turn off all topic notifications
			if (!hasRealTimeNotification()) {
				notification.activeLibraryTopics = [];
				notification.alert_schedule = false;
			} else if ((value === "true" || value === true) &&
				rtNotifications.includes(name) && !activeLibraryTopics.length) {
				notification.activeLibraryTopics = libraryTopics;
			}
		}

		setSettings(stateSettings);
	};

	const submitForm = () => {
		let poodle = new Fido();
		// Using poodle cause it's a throw away, like an avalanche poodle - don't want to abort call when unmounted
		poodle.fetch(`${authenticatedUser.profileUrlBase}/settings`, {
			method: "PUT",
			body: JSON.stringify(settings.notification)
		}, 'Saving Settings...', 'Saved.')
			// trying to set state when component is not mounted (backed up), causes potential memoery leak.
			// so, since we're backing up, we don't need to set the state.
			.then((data) => {})
			// TODO: probably only want to show some errors as snackbars
			.catch(error => showSnackbar(error.message));
	};

	let profileLink = (
		<Link
			aria-label="profile"
			onClick={gotoProfile}
			className={classes.link}>profile</Link>
	);

	return (
		<PaddedLayout className={classes.paddedLayout}>
			<GeneralSettingsPanel
				id="deliverySettings"
				expanded={selectedPanel === 'deliverySettings'}
				clickHandler={panelClicked('deliverySettings')}
				// headingIcon={["fad", "mail-bulk"]}
				heading="Delivery Settings"
				subHeading={
					<React.Fragment>
						This is where your email and text messages will be delivered and for what timezone to base the scheduling on. You can update this information from your {profileLink}.
					</React.Fragment>
				}
				expandedDetails={
					<DeliveryProfileSettings
						user={authenticatedUser} />
				} />

			<GeneralSettingsPanel
				id="realTimeMessaging"
				expanded={selectedPanel === 'realTimeMessaging'}
				clickHandler={panelClicked('realTimeMessaging')}
				headingIcon={["fad", "sms"]}
				heading="Near Real-Time Notifications"
				subHeading={
					<React.Fragment>
						Receive email and text messages for the products you are subscribed to.
						You can also configure them on a per topic basis and set a delivery schedule for certain times on particular days.
					</React.Fragment>
				}
				expandedDetails={
					<RealtimeNotificationSettings
						settings={settings}
						mobileNumber={authenticatedUser.cell_phone}
						rtNotifications={rtNotifications}
						notificationConfig={settings.notification}
						libraryTopics={libraryTopics}
						hasRealTimeNotification={hasRealTimeNotification()}
						formChangeHandler={handleFormChange}
						alertDaysButtonToggled={alertDaysButtonToggled}
						gotoProfile={gotoProfile} />
				} />

			<GeneralSettingsPanel
				id="emailDigests"
				expanded={selectedPanel === 'emailDigests'}
				clickHandler={panelClicked('emailDigests')}
				headingIcon={["fad", "file-alt"]}
				heading="Email Summary Digests"
				subHeading={
					<React.Fragment>
						Receive email summaries for content such as alerts and system performance.
						The summary of alerts can be configured to be sent on particular days at particular times.
					</React.Fragment>
				}
				expandedDetails={
					<EmailDigestSettings
						settings={settings}
						gotoProfile={gotoProfile}
						notificationConfig={settings.notification}
						formChangeHandler={handleFormChange}
						digestButtonToggled={digestButtonToggled} />
				} />


			<Grid container justifyContent="flex-end" className={classes.buttonGrid}>
				<Grid item>
					<Button
						color="primary"
						variant="contained"
						onClick={submitForm}>Save</Button>
				</Grid>
			</Grid>

		</PaddedLayout>
	);
}

GeneralSettings.propTypes = {
	history: PropTypes.object.isRequired,
	updateHeader: PropTypes.func.isRequired,
	resetHeader: PropTypes.func.isRequired,
	showSnackbar: PropTypes.func.isRequired
};

export default GeneralSettings;